<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Invoices</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Invoices</h3>

                    <DataTable
                        v-model:lazyParams="lazyParams"
                        :value="invoices"
                        lazy
                        paginator
                        size="small"
                        :ptOptions="{ mergeSections: true, classNameMergeStrategy: 'replace' }"
                        :pt="{headerCell: { class: 'py-1 px-2' }, bodyCell: { class: 'p-0' }, bodyRow: { class: 'p-0' }}"
                        :rows="10"
                        :totalRecords="totalRecords"
                        :loading="loading"
                        :rowsPerPageOptions="[10, 20, 50, 100]"
                        :sortMode="'single'"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        filterDisplay="menu"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'name', 'email', 'mobile']"
                        @page="onPage"
                        @sort="onSort"
                        @filter="onFilter">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header" @update:modelValue="onToggleColumns"
                                                     display="chip"
                                                     placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Dropdown v-model="selectedStandardFilter" :options="statusTypesLimited"
                                                  optionLabel="name" optionValue="code" placeholder="Select a Filter"
                                                  @update:modelValue="onToggleStandardFilters"/>
                                    </div>

                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Client Name, Client Preferred Name, Billing Plan Name, Scheduled, Description or Practitioner Name (Please press enter to search)"
                                                   style="width: 100%"
                                                   @keyup.enter="onSearch"/>

                                    </span>
                                </div>

                            </div>
                        </template>

                        <template #empty>
                            No invoices found.
                        </template>

                        <template #loading>
                            Loading invoices. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0" @click="$refs['menu' + index].toggle($event)" />
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="uniqueId" header="Invoice #" :sortable="true"/>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="invoiced" header="Invoiced" :sortable="true"/>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Unpaid</span>
                                <span v-if="data.status === 'S'"
                                      :class="'status-badge status-' + data.status">Submitted</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Paid</span>
                                <span v-if="data.status === 'U'"
                                      :class="'status-badge status-' + data.status">Unpaids Process</span>
                                <span v-if="data.status === 'C'"
                                      :class="'status-badge status-' + data.status">Archived</span>
                                <span v-if="data.status === 'M'"
                                      :class="'status-badge status-' + data.status">Member Liable</span>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-if="col.field === 'submissionMethod'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Submission Method</span>
                                    <span v-if="data.submissionMethod === 'C'"
                                          :class="'submission-badge status-' + data.submissionMethod">Client</span>
                                    <span v-if="data.submissionMethod === 'B'"
                                          :class="'submission-badge status-' + data.submissionMethod">Health Insurance</span>
                                </template>

                            </Column>

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-else-if="col.field === 'amount'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Amount</span>
                                    {{ formatAmount(data.amount) }}
                                </template>
                            </Column>

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-else-if="col.field === 'paid'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Amount</span>
                                    {{ formatAmount(data.paid) }}
                                </template>
                            </Column>

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <div class="grid invoice-body">
                        <div class="col-12">
                            <div id="invoice-content">
                                <div class="card invoice-wrapper">

                                    <div class="grid mx-0 invoice-header">
                                        <div class="col-5 logo-adress">
                                            &nbsp;
                                        </div>
                                        <div class="col-5 title-date">
                                            <h1 class="title">INVOICE</h1>
                                        </div>
                                    </div>

                                    <div class="card invoice-table billto-table">
                                        <div class="grid table-header">
                                            <div class="col-4">
                                                <h4 class="header">BILL TO </h4>
                                            </div>
                                            <div class="col-4">
                                                <h4 class="header">DATE </h4>
                                            </div>
                                            <div class="col-2 text-right">
                                                <h4 class="header">INVOICE NO </h4>
                                            </div>
                                        </div>
                                        <div class="grid table-content-row">
                                            <div class="col-4">
                                                <p>{{ invoice.clientName }}</p>
                                            </div>
                                            <div class="col-4">
                                                <p>{{ invoice.invoiced }}</p>
                                            </div>
                                            <div class="col-2 text-right">
                                                <p>{{ invoice.uniqueId }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <hr/>

                                    <div v-show="companyConfiguration.type!='H'"
                                         class="card invoice-table products-table">
                                        <div class="grid mx-0 table-header">
                                            <div class="col-1 row-title">
                                                <h4 class="header">Date </h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Quantity </h4>
                                            </div>
                                            <div class="col-3 row-title">
                                                <h4 class="header">Description </h4>
                                            </div>
                                            <div class="col-2 row-title">
                                                <h4 class="header">Unit Price </h4>
                                            </div>
                                            <div class="col-2 row-title">
                                                <h4 class="header">Discount </h4>
                                            </div>
                                            <div class="col-1 row-title text-right">
                                                <h4 class="header">Amount </h4>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-show="companyConfiguration.type!='H'"
                                         v-for="invoiceComponent in invoiceComponents"
                                         :key="invoiceComponent.id">
                                        <!-- Render each invoice item here -->

                                        <div class="grid mx-0 table-content-row even">
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.invoiced }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.quantity }} </p>
                                            </div>
                                            <div class="col-3 text-left">
                                                <p>{{ invoiceComponent.description }} </p>
                                            </div>
                                            <div class="col-2 text-left">
                                                <p>{{ formatAmount(invoiceComponent.unitCost) }}</p>
                                            </div>
                                            <div class="col-2 text-left">
                                                <p>{{ formatAmount(invoiceComponent.discount) }}</p>
                                            </div>
                                            <div class="col-1 text-right">
                                                <p>{{ formatAmount(invoiceComponent.amount) }} </p>
                                            </div>
                                        </div>

                                    </div>

                                    <div v-show="companyConfiguration.type==='H'"
                                         class="card invoice-table products-table">
                                        <div class="grid mx-0 table-header">
                                            <div class="col-1 row-title">
                                                <h4 class="header">Date </h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Quantity </h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Procedure Code</h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Modifier</h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">NAPPI Code</h4>
                                            </div>
                                            <div class="col-2 row-title">
                                                <h4 class="header">Description </h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Unit Price </h4>
                                            </div>
                                            <div class="col-1 row-title">
                                                <h4 class="header">Discount </h4>
                                            </div>
                                            <div class="col-1 row-title text-right">
                                                <h4 class="header">Amount </h4>
                                            </div>
                                        </div>
                                    </div>


                                    <div v-show="companyConfiguration.type==='H'"
                                         v-for="invoiceComponent in invoiceComponents"
                                         :key="invoiceComponent.id">
                                        <!-- Render each invoice item here -->

                                        <div class="grid mx-0 table-content-row even">
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.invoiced }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.quantity }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.bhfCode }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.modifier }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ invoiceComponent.nappiCode }} </p>
                                            </div>
                                            <div class="col-2 text-left">
                                                <p>{{ invoiceComponent.description }} </p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ formatAmount(invoiceComponent.unitCost) }}</p>
                                            </div>
                                            <div class="col-1 text-left">
                                                <p>{{ formatAmount(invoiceComponent.discount) }}</p>
                                            </div>
                                            <div class="col-1 text-right">
                                                <p>{{ formatAmount(invoiceComponent.amount) }} </p>
                                            </div>
                                        </div>

                                    </div>

                                    <div
                                        v-if="invoiceComponents === null || invoiceComponents.length === 0">
                                        <!-- Handle case where invoices is null or empty -->
                                        No invoice components available.
                                    </div>

                                </div>
                                <hr/>

                                <div class="card invoice-table billto-table">
                                    <div class="grid table-header">
                                        <div class="col-8 text-right">
                                            &nbsp;
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">Subtotal </h4>
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">{{ formatAmount(invoice.amount - invoice.tax) }}</h4>
                                        </div>
                                    </div>

                                    <div class="grid table-header">
                                        <div class="col-8 text-right">
                                            &nbsp;
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">Tax</h4>
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">{{ formatAmount(invoice.tax) }}</h4>
                                        </div>
                                    </div>
                                    <div class="grid table-header">
                                        <div class="col-8 text-right">
                                            &nbsp;
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">Total</h4>
                                        </div>
                                        <div class="col-1 text-right">
                                            <h4 class="header">{{ formatAmount(invoice.amount) }}</h4>
                                        </div>
                                    </div>

                                </div>

                                <hr/>

                            </div>
                        </div>
                    </div>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Email Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Are you sure that you want to email this
                            invoice?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Email" class="mr-2 mb-2" :loading="loadingResponse" @click="emailInvoice"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                    <div v-if="paymentLink">
                        <h4>ZAR Online Payment Link</h4>
                        <div class="field">
                            <a :href="paymentLink" target="_blank">{{ paymentLink }}</a>
                        </div>
                    </div>

                    <div v-if="gbpPaymentLink">
                        <h4>GBP Online Payment Link</h4>
                        <div class="field">
                            <a :href="gbpPaymentLink" target="_blank">{{ gbpPaymentLink }}</a>
                        </div>
                    </div>

                    <div v-if="usdPaymentLink">
                        <h4>USD Online Payment Link</h4>
                        <div class="field">
                            <a :href="usdPaymentLink" target="_blank">{{ usdPaymentLink }}</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Download Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Are you sure that you want to download this
                            invoice?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Download" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="downloadInvoice"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Status and Date of Invoice {{ this.invoice.uniqueId }} for {{
                            this.invoice.clientName
                        }}</h3>

                    <div class="field">
                        <label for="invoiced">Invoice Date</label>
                        <Calendar id="invoiced" v-model="parsedInvoiced" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="invoiceStatus">Select the status?</label>
                        <Dropdown v-model="invoice.status" :options="statusTypesLimited"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="updateInvoiceStatus"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <DataTable
                        :value="invoiceComponents"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        responsiveLayout="scroll">

                        <template #empty>
                            No invoice components found.
                        </template>

                        <template #loading>
                            Loading invoice components. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <div v-if="(data.status != 'P')">
                                    <SplitButton icon="pi pi-pencil" label="Select"
                                                 :model="getFilteredItemsComponents(data)"
                                                 severity="success"
                                                 @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                                </div>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'S'"
                                      :class="'status-badge status-' + data.status">Scheduled</span>
                                <span v-if="data.status === 'C'"
                                      :class="'status-badge status-' + data.status">Completed</span>
                                <span v-if="data.status === 'M'"
                                      :class="'status-badge status-' + data.status">Missed</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Invoiced</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Paid</span>
                                <span v-if="data.status === 'U'"
                                      :class="'status-badge status-' + data.status">Unpaid</span>
                                <span v-if="data.status === 'M'"
                                      :class="'status-badge status-' + data.status">Member Liable</span>
                            </template>
                        </Column>

                        <Column field="invoiced" header="Date" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Date</span>
                                {{ data.invoiced }}
                            </template>
                        </Column>

                        <Column field="quantity" header="Quantity" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Quantity</span>
                                {{ data.quantity }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration === 'H'" field="bhfCode" header="BHF Code"
                                :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">BHF Code</span>
                                {{ data.bhfCode }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration === 'H'" field="nappiCode" header="NAPPI Code"
                                :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">NAPPI Code</span>
                                {{ data.nappiCode }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration === 'H'" field="modifier" header="Modifier"
                                :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Modifier</span>
                                {{ data.modifier }}
                            </template>
                        </Column>

                        <Column field="unitCost" header="Unit Cost" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Unit Cost</span>
                                {{ formatAmount(data.unitCost) }}
                            </template>
                        </Column>

                        <Column field="discount" header="Discount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Discount</span>
                                {{ formatAmount(data.discount) }}
                            </template>
                        </Column>

                        <Column field="amount" header="Amount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Amount</span>
                                {{ formatAmount(data.amount) }}
                            </template>
                        </Column>

                        <Column field="paid" header="Paid" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Paid</span>
                                {{ formatAmount(data.paid) }}
                            </template>
                        </Column>

                        <Column field="description" header="Description" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Description</span>
                                {{ data.description }}
                            </template>
                        </Column>

                    </DataTable>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Are you sure that you want to delete this
                            invoice?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="loadingResponse" @click="deleteInvoice"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Pay Invoice {{ this.invoice.uniqueId }} for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="paid">Paid Date</label>
                        <Calendar id="paid" v-model="newPayment.paid" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newPayment.type" :options="paymentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newPayment.description" required="true"/>
                    </div>

                    <Button label="Pay" class="mr-2 mb-2" :loading="loadingResponse" @click="payInvoice"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 9" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Claim for Invoice {{ this.invoice.uniqueId }} of {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Are you sure that you want to create a claim for this
                            invoice?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(confirmationResponse==='Y')"
                         class="field">
                        <label for="healthInsuranceSubmission">Health Insurance Submission Method</label>
                        <Dropdown v-model="healthInsuranceSubmissionMethodResponse"
                                  :options="healthInsuranceSubmissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="(confirmationResponse==='Y') && company && company.taxRegistered === 'Y'" class="field">
                        <label for="useTaxRate">Use Tax Rate?</label>
                        <Dropdown v-model="useTaxRate" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="loadingResponse" @click="createClaim"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 10" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Add Billing Plan to Invoice {{ this.invoice.uniqueId }} of {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="billingPlan"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <div v-if="company && company.taxRegistered === 'Y'" class="field">
                        <label for="useTaxRate">Use Tax Rate?</label>
                        <Dropdown v-model="useTaxRate" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Add" class="mr-2 mb-2" :loading="loadingResponse" @click="addBillingPlan"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 11" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Add Product to Invoice {{ this.invoice.uniqueId }} of {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="product">Product</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="product"
                                      :suggestions="filteredProducts"
                                      @complete="searchProduct($event)" field="name"/>
                    </div>

                    <div v-if="company && company.taxRegistered === 'Y'" class="field">
                        <label for="useTaxRate">Use Tax Rate?</label>
                        <Dropdown v-model="useTaxRate" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Add" class="mr-2 mb-2" :loading="loadingResponse" @click="addProduct"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 12" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Invoice Component {{ this.invoiceComponent.id }} of Invoice {{ this.invoice.uniqueId }}
                        for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="quantity">Quantity</label>
                        <InputText id="quantity" v-model.trim="invoiceComponent.quantity"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="invoiceComponent.description"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="bhfCode">BHF Code</label>
                        <InputText id="bhfCode" v-model.trim="invoiceComponent.bhfCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="nappiCode">NAPPI Code</label>
                        <InputText id="nappiCode" v-model.trim="invoiceComponent.nappiCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="modifier">Modifier</label>
                        <InputText id="modifier" v-model.trim="invoiceComponent.modifier"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="unitCost">Unit Cost</label>
                        <InputText id="unitCost" v-model.trim="invoiceComponent.unitCost"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="discountPercentage">Discount (%)</label>
                        <InputText id="discountPercentage" v-model.trim="invoiceComponent.discountPercentage"
                                   required="true"/>

                        <!--                        <InputNumber id="discountPercentage" v-model.trim="invoiceComponent.discountPercentage"-->
                        <!--                                     :minFractionDigits="2" :maxFractionDigits="5" :min="0" :max="100"/>-->
                    </div>

                    <br/>
                    <h3>Diagnostic Codes</h3>

                    <div class="field">
                        <label for="icd10Code1">ICD10 Code (Primary)</label>
                        <AutoComplete v-model="invoiceComponent.diagnosisCode1"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div class="field">
                        <label for="icd10Code2">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="invoiceComponent.diagnosisCode2"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div class="field">
                        <label for="icd10Code3">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="invoiceComponent.diagnosisCode3"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div class="field">
                        <label for="icd10Code4">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="invoiceComponent.diagnosisCode4"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div class="field">
                        <label for="icd10Code5">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="invoiceComponent.diagnosisCode5"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="updateInvoiceComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="callResetInvoiceComponents"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 13" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Invoice Component {{ this.invoiceComponent.id }} of Invoice {{ this.invoice.uniqueId }}
                        for {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Are you sure that you want to delete this invoice
                            component?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="deleteInvoiceComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="callResetInvoiceComponents"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 14" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Pay Invoice Component {{ this.invoiceComponent.id }} of Invoice {{ this.invoice.uniqueId }} for
                        {{ this.invoice.clientName }}</h3>

                    <div class="field">
                        <label for="paid">Paid Date</label>
                        <Calendar id="paid" v-model="newPayment.paid" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newPayment.type" :options="paymentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="amount">Amount</label>
                        <InputText id="amount" v-model.trim="newPayment.amount" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newPayment.description" required="true"/>
                    </div>

                    <Button label="Pay" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="payInvoiceComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="loadingResponse"
                            @click="callResetInvoiceComponents"></Button>

                </div>
            </div>
        </div>


    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import InvoiceService from "@/service/InvoiceService";
import numeral from "numeral";
import InvoiceComponentService from "@/service/InvoiceComponentService";
import BillingPlanService from "@/service/BillingPlanService";
import ProductService from "@/service/ProductService";
import Icd10CodeService from "@/service/Icd10CodeService";
import moment from "moment/moment";
import ClaimService from "@/service/ClaimService";
import dayjs from "dayjs";
import PaymentService from "@/service/PaymentService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    props: {
        inputAppointmentId: {
            type: String,
            required: false
        },
        inputInvoiceId: {
            type: String,
            required: false
        }
    },
    computed: {
        parsedInvoiced: {
            get() {
                return new Date(this.invoice.invoiced);
            },
            set(value) {
                this.invoice.invoiced = value instanceof Date ? value.toISOString() : '';
            }
        }
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
        this.icd10CodeService = new Icd10CodeService();
    },
    async mounted() {
        try {
            await UserService.getUserPing();
        } catch (e) {
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            totalRecords: 0,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            profile: {},
            submitted: false,
            invoices: null,
            originalInvoices: null,
            invoice: {},
            userConfiguration: {},
            company: {},
            companyConfiguration: {},
            filters: null,
            loadingResponse: false,
            activeTab: 1,
            confirmationResponse: 'N',
            useTaxRate: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            filteredItemsCache: new Map(),
            filteredItemsComponentsCache: new Map(),
            healthInsuranceSubmissionMethodResponse: 'E',
            invoiceComponents: null,
            invoiceComponent: {},
            paymentLink: null,
            gbpPaymentLink: null,
            usdPaymentLink: null,
            newInvoice: {},
            newPayment: {},
            billingPlans: null,
            billingPlan: {},
            products: null,
            product: {},
            filteredBillingPlans: [],
            filteredProducts: [],
            filteredDiagnosticCodes: [],
            statusTypes: [
                {name: 'Paid', code: 'P'},
                {name: 'Unpaid', code: 'I'},
                {name: 'Submitted', code: 'S'},
                {name: 'Unpaids Process', code: 'U'},
                {name: 'Archived', code: 'C'}
            ],
            statusTypesLimited: [
                {name: 'All', code: 'A'},
                {name: 'Paid', code: 'P'},
                {name: 'Unpaid', code: 'I'},
                {name: 'Submitted', code: 'S'},
                {name: 'Member Liable', code: 'M'}
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            paymentTypes: [
                {name: 'Credit Card', code: 'C'},
                {name: 'Bank Transfer', code: 'B'},
                {name: 'Health Insurance', code: 'H'},
                {name: 'Cryptocurrency', code: 'R'},
                {name: 'Cash', code: 'A'},
                {name: 'Other', code: 'O'},
                {name: 'Write-Off', code: 'W'},
                {name: 'Credit', code: 'E'},
                {name: 'Helpmed Hulpfonds', code: 'Z'}
            ],
        }
    },
    watch: {
        invoices() {
            this.filteredItemsCache.clear();
        },
        invoiceComponents() {
            this.filteredItemsComponentsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            const [userConfigurationData, companyConfigurationData, companyData, productsData, billingPlansData] = await Promise.all([
                UserService.getUserConfig(this.userStore.user.id),
                CompanyService.getCompanyConfiguration(this.profile.companyId),
                CompanyService.getCompany(this.profile.companyId),
                ProductService.getCompanyProducts(this.profile.companyId),
                BillingPlanService.getActiveBillingPlans(this.profile.companyId)
            ]);

            this.userConfiguration = userConfigurationData.data;
            this.companyConfiguration = companyConfigurationData.data;
            this.company = companyData.data;
            this.products = productsData.data;
            this.billingPlans = billingPlansData.data;

            this.columnsDefault = [
                {field: 'clientName', header: 'Client Name', sortable: true},
                {field: 'amount', header: 'Amount', sortable: true},
                {field: 'paid', header: 'Paid', sortable: true},
                {field: 'submissionMethod', header: 'Submission Method', sortable: true},
                {field: 'appointmentDate', header: 'Appointment Date', sortable: false}
            ];

            if (this.companyConfiguration.type === 'H') {
                this.columnsFullList = [
                    {field: 'clientName', header: 'Client Name', sortable: true},
                    {field: 'amount', header: 'Amount', sortable: true},
                    {field: 'paid', header: 'Paid', sortable: true},
                    {field: 'submissionMethod', header: 'Submission Method', sortable: true},
                    {field: 'appointmentDate', header: 'Appointment Date', sortable: false},
                    {field: 'practitionerName', header: 'Practitioner Name', sortable: true},
                    {field: 'bankAccountAccountHolder', header: 'Bank Account Holder Name', sortable: true}
                ];

                this.standardFilters = [
                    {field: 'unpaid', header: 'Unpaid'},
                    {field: 'paid', header: 'Paid'},
                    {field: 'memberLiable', header: 'Member Liable'}
                ];

            } else {
                this.columnsFullList = [
                    {field: 'clientName', header: 'Client Name', sortable: true},
                    {field: 'amount', header: 'Amount', sortable: true},
                    {field: 'paid', header: 'Paid', sortable: true},
                    {field: 'submissionMethod', header: 'Submission Method', sortable: true},
                    {field: 'appointmentDate', header: 'Appointment Date', sortable: true},
                    {field: 'bankAccountAccountHolder', header: 'Bank Account Holder Name', sortable: true}
                ];

                this.standardFilters = [
                    {field: 'unpaid', header: 'Unpaid'},
                    {field: 'paid', header: 'Paid'}
                ];
            }

            this.selectedColumns = this.columnsDefault;

            if (this.inputInvoiceId != null) {
                const invoiceData = await InvoiceService.getInvoice(this.inputInvoiceId);

                this.invoices = [invoiceData.data];
                this.originalInvoices = [...this.invoices]

            } else if (this.inputAppointmentId != null) {
                const invoiceData = await InvoiceService.getInvoiceforAppointment(this.inputAppointmentId);

                this.invoices = [invoiceData.data];
                this.originalInvoices = [...this.invoices]
            } else {
                await this.loadData();
            }

            this.initFilters();

            this.loading = false;
        },
        callReset() {
            this.activeTab = 1;
        },
        callResetInvoiceComponents() {
            this.activeTab = 6;
        },
        async loadData() {
            this.loading = true;
            try {
                if (this.userStore.user.rights === 'M') {
                    const response = await InvoiceService.getAllPractitionerInvoicesLazyLoading(
                        this.userStore.user.practitionerId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.invoices = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalInvoices = this.invoices;

                    this.totalRecords = response.data?.totalElements || 0;

                } else {
                    const response = await InvoiceService.getAllCompanyInvoicesLazyLoading(
                        this.profile.companyId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    this.invoices = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalInvoices = this.invoices;

                    this.totalRecords = response.data?.totalElements || 0;
                }

            } catch (error) {
                this.invoices = [];
                this.originalInvoices = [];
                this.totalRecords = 0;
            }
            this.loading = false;
        },
        onPage(event) {
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            this.loadData();
        },
        onFilter(event) {
            this.lazyParams.filters = event.filters;
            this.loadData();
        },
        onSearch() {
            this.lazyParams.first = 0;
            this.loadData();
        },
        onToggleStandardFilters() {
            this.lazyParams.first = 0;
            this.loadData();
        },
        clearFilter() {
            this.initFilters();
            this.lazyParams.filters = {}; // Clear all column filters
            this.lazyParams.first = 0; // Reset pagination to first page
            this.loadData();
        },
        restoreToOriginal() {
            this.initFilters();
            this.lazyParams.filters = {}; // Clear all column filters
            this.lazyParams.first = 0; // Reset pagination to first page
            this.loadData();
            // window.location.reload();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'invoiced': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'uniqueId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'View',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewInvoice(data);
                }
            });

            items.push({
                label: 'Email',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callEmailInvoice(data);
                }
            });

            items.push({
                label: 'Download',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDownloadInvoice(data);
                }
            });

            if (this.userConfiguration.enableInvoicing != 'N') {
                items.push({
                    label: 'Update Status/Date',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateInvoiceStatus(data);
                    }
                });

                items.push({
                    label: 'View Components',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateInvoice(data);
                    }
                });
            }

            if ((data.status != 'P') && (data.numberOfClaims === 0)) {

                if (this.userConfiguration.enableInvoicing != 'N') {
                    items.push({
                        label: 'Delete',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callDeleteInvoice(data);
                        }
                    });
                }
            }

            if (data.status != 'P') {
                if (this.userConfiguration.enableInvoicing != 'N') {
                    items.push({
                        label: 'Pay in Full',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callPayInvoice(data);
                        }
                    });

                }

                if (this.companyConfiguration.type === 'H') {
                    if (this.userConfiguration.enableInvoicing != 'N') {
                        items.push({
                            label: 'Create Claim',
                            class: 'mb-4 mr-4 ml-4 mt-4',
                            command: () => {
                                this.callCreateClaim(data);
                            }
                        });
                    }
                }

                if (this.userConfiguration.enableInvoicing != 'N') {
                    items.push({
                        label: 'Add Billing Plan',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callAddBillingPlan(data);
                        }
                    });

                    items.push({
                        label: 'Add Product',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callAddProduct(data);
                        }
                    });
                }

                // items.push({
                //     label: 'Create Follow-up',
                //     class: 'mb-4 mr-4 ml-4 mt-4',
                //     command: () => {
                //         this.callDownloadInvoice(data);
                //     }
                // });
            }

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        getFilteredItemsComponents(data) {
            if (this.filteredItemsComponentsCache.has(data.id)) {
                return this.filteredItemsComponentsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateInvoiceComponent(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteInvoiceComponent(data);
                }
            });

            items.push({
                label: 'Pay',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callPayInvoiceComponent(data);
                }
            });

            this.filteredItemsComponentsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection() {
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        convertDateIntoLocalTimezone(inputDate) {
            try {
                const moment = require("moment-timezone");
                const inputTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                let targetTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    targetTimeZone = "Africa/Johannesburg";
                }

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                return inputDate;
            }

        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            } else {
                return numeral(String(0)).format('0,0.00');
            }
        },
        async searchBillingPlan(event) {
            const [responseData] = await Promise.all([
                BillingPlanService.searchBillingPlans(this.profile.companyId, event.query.toLowerCase())
            ]);

            this.filteredBillingPlans = responseData.data;
        },
        async searchProduct(event) {
            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                this.filteredProducts = (this.products ?? []).slice(0, 50); // cap if large
                return;
            }

            ProductService.searchProducts(this.profile.companyId, q)
                .then(data => {
                    this.filteredProducts = data.data;
                })
                .catch(() => {
                    this.filteredProducts = [];
                });

        },
        async searchDiagnosticCodes(event) {
            const [responseData] = await Promise.all([
                this.icd10CodeService.getIcd10CodesSimple(event.query.toLowerCase())
            ]);

            this.filteredDiagnosticCodes = responseData.data;
        },
        async callViewInvoice(data) {
            this.activeTab = 2;
            this.invoice = data;
            this.confirmationResponse = 'N';

            const [responseData] = await Promise.all([
                InvoiceComponentService.getInvoiceComponentsForInvoice(this.invoice.id)
            ]);

            this.invoiceComponents = responseData.data;
        },
        async callEmailInvoice(data) {
            this.activeTab = 3;
            this.invoice = data;
            this.confirmationResponse = 'N';

            if (this.invoice.status !== 'P' && this.companyConfiguration.payfastMerchantId) {
                const responseData = await InvoiceService.getPayNowLink(this.invoice.id);

                this.paymentLink = responseData.data;
            }

            if (this.invoice.status !== 'P' && this.companyConfiguration.dnaPaymentsClientId) {
                const responseData = await InvoiceService.getGbpPayNowLink(this.invoice.id);

                this.gbpPaymentLink = responseData.data;
            }

            if (this.invoice.status !== 'P' && this.companyConfiguration.stripeApiSecret) {
                const responseData = await InvoiceService.getUsdPayNowLink(this.invoice.id);

                this.usdPaymentLink = responseData.data;
            }
        },
        callDownloadInvoice(data) {
            this.activeTab = 4;
            this.invoice = data;
            this.confirmationResponse = 'N';
        },
        callUpdateInvoiceStatus(data) {
            this.activeTab = 5;
            this.invoice = data;
            this.confirmationResponse = 'N';
        },
        async callUpdateInvoice(data) {
            this.activeTab = 6;
            this.invoice = data;
            this.confirmationResponse = 'N';

            const [responseData] = await Promise.all([
                InvoiceComponentService.getInvoiceComponentsForInvoice(this.invoice.id)
            ]);

            this.invoiceComponents = responseData.data;

        },
        callDeleteInvoice(data) {
            this.activeTab = 7;
            this.invoice = data;
            this.confirmationResponse = 'N';
        },
        callPayInvoice(data) {
            this.activeTab = 8;
            this.invoice = data;
            this.confirmationResponse = 'N';

            this.newPayment = {};
            this.newPayment.paid = moment().toDate();
            this.newPayment.paid.setMinutes(0);
            this.newPayment.type = 'B';
            this.newPayment.description = '';
        },
        callCreateClaim(data) {
            this.activeTab = 9;
            this.invoice = data;
            this.confirmationResponse = 'N';
        },
        callAddBillingPlan(data) {
            this.activeTab = 10;
            this.invoice = data;
            this.useTaxRate = this.company.taxRegistered;

            if (this.billingPlans && this.billingPlans.length > 0) {
                this.billingPlan = this.billingPlans[0];
            }
        },
        callAddProduct(data) {
            this.activeTab = 11;
            this.invoice = data;
            this.useTaxRate = this.company.taxRegistered;

            if (this.products && this.products.length > 0) {
                this.product = this.products[0];
            }

        },
        callUpdateInvoiceComponent(data) {
            this.activeTab = 12;
            this.invoiceComponent = data;
            this.confirmationResponse = 'N';
        },
        callDeleteInvoiceComponent(data) {
            this.activeTab = 13;
            this.invoiceComponent = data;
            this.confirmationResponse = 'N';
        },
        callPayInvoiceComponent(data) {
            this.activeTab = 14;
            this.invoiceComponent = data;
            this.confirmationResponse = 'N';

            this.newPayment.paid = moment().toDate();
            this.newPayment.paid.setMinutes(0);
            this.newPayment.type = 'B';
            this.newPayment.amount = this.invoiceComponent.amount - this.invoiceComponent.paid;
            this.newPayment.description = '';

        },
        async emailInvoice() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {

                        await InvoiceService.emailInvoice(this.invoice.id);

                        this.loadData();

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Emailed invoice',
                            life: 3000
                        });
                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to email invoice. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async downloadInvoice() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {

                        const response = await InvoiceService.downloadInvoice(this.invoice.id);

                        const blob = new Blob([response.data], {type: 'application/pdf'});

                        // Create a link element and simulate a click to trigger the download
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(blob);
                        link.download = this.invoice.uniqueId + '.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        this.loadData();
                        // const [responseData] = await Promise.all([
                        //     InvoiceService.getAllCompanyInvoices(this.profile.companyId)
                        // ]);
                        //
                        // this.invoices = responseData.data;
                        // this.originalInvoices = responseData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Downloaded invoice',
                            life: 3000
                        });
                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to download invoice. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async updateInvoiceStatus() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                try {

                    this.invoice.invoiced = moment(this.invoice.invoiced).format('YYYY-MM-DD HH:mm');

                    await InvoiceService.updateInvoice(this.invoice.id, this.invoice);

                    this.loadData();

                    // const [responseData] = await Promise.all([
                    //     InvoiceService.getAllCompanyInvoices(this.profile.companyId)
                    // ]);
                    //
                    // this.invoices = responseData.data;
                    // this.originalInvoices = responseData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated invoice',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update invoice. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async deleteInvoice() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {

                        await InvoiceService.deleteInvoice(this.invoice.id);

                        this.loadData();

                        // const [responseData] = await Promise.all([
                        //     InvoiceService.getAllCompanyInvoices(this.profile.companyId)
                        // ]);
                        //
                        // this.invoices = responseData.data;
                        // this.originalInvoices = responseData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted invoice',
                            life: 3000
                        });
                    } catch (error) {

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete invoice. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async payInvoice() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                try {
                    var newPaymentDto = {
                        description: this.newPayment.description,
                        type: this.newPayment.type,
                        amount: this.invoice.amount - this.invoice.paid,
                        paid: dayjs(this.newPayment.paid, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
                    };

                    await PaymentService.createPaymentForInvoice(this.profile.companyId, this.invoice.id, newPaymentDto);

                    this.loadData();

                    // const [responseData] = await Promise.all([
                    //     InvoiceService.getAllCompanyInvoices(this.profile.companyId)
                    // ]);
                    //
                    // this.invoices = responseData.data;
                    // this.originalInvoices = responseData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Allocated payment to invoice',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to allocate payment to invoice. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async createClaim() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {

                        await ClaimService.createClaim(this.invoice.id, this.healthInsuranceSubmissionMethodResponse.trim(), this.useTaxRate);

                        this.loadData();

                        // const [responseData] = await Promise.all([
                        //     InvoiceService.getAllCompanyInvoices(this.profile.companyId)
                        // ]);
                        //
                        // this.invoices = responseData.data;
                        // this.originalInvoices = responseData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Created claim for invoice',
                            life: 3000
                        });
                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to create claim for invoice. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async addBillingPlan() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                try {

                    var tempBillingPlan = "";

                    try {
                        tempBillingPlan = this.billingPlan.id;
                    } catch (error) {
                        tempBillingPlan = "";
                    }

                    await InvoiceService.addBillingPlanToInvoice(this.invoice.id, this.useTaxRate, tempBillingPlan);

                    this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Added billing plan to invoice',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to add billing plan to invoice. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async addProduct() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoice.id) {

                try {
                    var tempProduct = "";

                    try {
                        tempProduct = this.product.id;
                    } catch (error) {
                        tempProduct = "";
                    }

                    await InvoiceService.addProductToInvoice(this.invoice.id, this.useTaxRate, tempProduct);

                    this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Added product to invoice',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to add product to invoice. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoice = {};
            this.activeTab = 1;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async updateInvoiceComponent() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoiceComponent.id) {

                try {

                    var tempDiagnosisCode1 = "";
                    var tempDiagnosisCode2 = "";
                    var tempDiagnosisCode3 = "";
                    var tempDiagnosisCode4 = "";
                    var tempDiagnosisCode5 = "";

                    if (this.invoiceComponent.diagnosisCode1 != null) {
                        if (this.invoiceComponent.diagnosisCode1.length > 1) {
                            tempDiagnosisCode1 = this.invoiceComponent.diagnosisCode1;
                        } else {
                            try {
                                tempDiagnosisCode1 = this.invoiceComponent.diagnosisCode1.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode1 = this.invoiceComponent.diagnosisCode1;
                            }
                        }
                    }

                    if (this.invoiceComponent.diagnosisCode2 != null) {
                        if (this.invoiceComponent.diagnosisCode2.length > 1) {
                            tempDiagnosisCode2 = this.invoiceComponent.diagnosisCode2;
                        } else {
                            try {
                                tempDiagnosisCode2 = this.invoiceComponent.diagnosisCode2.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode2 = this.invoiceComponent.diagnosisCode2;
                            }
                        }
                    }

                    if (this.invoiceComponent.diagnosisCode3 != null) {
                        if (this.invoiceComponent.diagnosisCode3.length > 1) {
                            tempDiagnosisCode3 = this.invoiceComponent.diagnosisCode3;
                        } else {

                            try {
                                tempDiagnosisCode3 = this.invoiceComponent.diagnosisCode3.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode3 = this.invoiceComponent.diagnosisCode3;
                            }
                        }
                    }

                    if (this.invoiceComponent.diagnosisCode4 != null) {
                        if (this.invoiceComponent.diagnosisCode4.length > 1) {
                            tempDiagnosisCode4 = this.invoiceComponent.diagnosisCode4;
                        } else {

                            try {
                                tempDiagnosisCode4 = this.invoiceComponent.diagnosisCode4.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode4 = this.invoiceComponent.diagnosisCode4;
                            }
                        }
                    }

                    if (this.invoiceComponent.diagnosisCode5 != null) {
                        if (this.invoiceComponent.diagnosisCode5.length > 1) {
                            tempDiagnosisCode5 = this.invoiceComponent.diagnosisCode5;
                        } else {

                            try {
                                tempDiagnosisCode5 = this.invoiceComponent.diagnosisCode5.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode5 = this.invoiceComponent.diagnosisCode5;
                            }
                        }
                    }

                    var updateInvoiceComponentDto = {
                        diagnosisCode1: tempDiagnosisCode1,
                        diagnosisCode2: tempDiagnosisCode2,
                        diagnosisCode3: tempDiagnosisCode3,
                        diagnosisCode4: tempDiagnosisCode4,
                        diagnosisCode5: tempDiagnosisCode5,
                        quantity: this.invoiceComponent.quantity,
                        unitCost: this.invoiceComponent.unitCost,
                        bhfCode: this.invoiceComponent.bhfCode,
                        nappiCode: this.invoiceComponent.nappiCode,
                        modifier: this.invoiceComponent.modifier,
                        discountPercentage: this.invoiceComponent.discountPercentage,
                        description: this.invoiceComponent.description
                    };

                    await InvoiceComponentService.updateInvoiceComponent(this.invoiceComponent.id, updateInvoiceComponentDto);

                    this.loadData();

                    const [componentsData] = await Promise.all([
                        InvoiceComponentService.getInvoiceComponentsForInvoice(this.invoice.id)
                    ]);

                    this.invoiceComponents = componentsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated invoice component',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update invoice component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoiceComponent = {};
            this.activeTab = 6;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async deleteInvoiceComponent() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoiceComponent.id) {

                if (this.confirmationResponse.trim() === "Y") {
                    try {

                        await InvoiceComponentService.deleteInvoiceComponent(this.invoiceComponent.id);

                        this.loadData();

                        const [componentsData] = await Promise.all([
                            InvoiceComponentService.getInvoiceComponentsForInvoice(this.invoice.id)
                        ]);

                        this.invoiceComponents = componentsData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted invoice component',
                            life: 3000
                        });
                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete invoice component. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.invoiceComponent = {};
            this.activeTab = 6;
            this.submitted = false;
            this.loadingResponse = false;
        },
        async payInvoiceComponent() {
            this.submitted = true;
            this.loadingResponse = true;

            if (this.invoiceComponent.id) {

                try {

                    var newPaymentDto = {
                        description: this.newPayment.description,
                        type: this.newPayment.type,
                        amount: this.newPayment.amount,
                        paid: dayjs(this.newPayment.paid, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
                    };

                    await PaymentService.createPaymentForInvoiceComponent(this.profile.companyId, this.invoiceComponent.invoiceId,
                        this.invoiceComponent.id, newPaymentDto);

                    this.loadData();

                    const [componentsData] = await Promise.all([
                        InvoiceComponentService.getInvoiceComponentsForInvoice(this.invoice.id)
                    ]);

                    this.invoiceComponents = componentsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Allocated payment for invoice component',
                        life: 3000
                    });
                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to allocate payment for invoice component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.invoiceComponent = {};
            this.activeTab = 6;
            this.submitted = false;
            this.loadingResponse = false;
        }
    }

};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .submission-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-I {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-W {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-U {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-F {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-M {
        background: #FFD8B2;
        color: #805B36;
    }

    &.status-H {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-H {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-C {
        background: #d9b78a;
        color: #b94f03;
    }

}

.submission-badge {
    &.status-C {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }
}

</style>

